import { Alert, Button, Empty, Skeleton, Typography } from 'antd';
import { useGetPublicArtifactCollectionQuery } from 'api/ArtifactsApi';
import { useGetDesignConfigQuery } from 'api/DesignApi';
import PublicArtifactCollectionRenderer from 'components/Artifacts/PublicArtifactCollectionRenderer';
import ContainerComponent from 'components/Tool/ContainerComponent/ContainerComponent';
import { ReactNode, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  getArtifactTokenFromHash,
  getArtifactTokenFromSearch,
  getCanonicalArtifactPathFromHash,
  getCanonicalArtifactPathFromQuery
} from 'utils/publicRoutePrivacy';
import { ensureHttp } from 'utils/utils';

const { Paragraph, Title } = Typography;

export function PublicArtifactCollectionPage() {
  const navigate = useNavigate();
  const { token: tokenFromParams } = useParams();
  const { pathname, search, hash } = useLocation();

  const hashToken = getArtifactTokenFromHash(hash);
  const queryToken = getArtifactTokenFromSearch(search);
  const token = tokenFromParams || hashToken || queryToken;

  useEffect(() => {
    const canonicalPathFromHash = getCanonicalArtifactPathFromHash(hash);
    if (!tokenFromParams && canonicalPathFromHash) {
      navigate(canonicalPathFromHash, { replace: true });
      return;
    }

    const canonicalPathFromQuery = getCanonicalArtifactPathFromQuery(pathname, search);
    if (!tokenFromParams && !hashToken && canonicalPathFromQuery) {
      navigate(canonicalPathFromQuery, { replace: true });
    }
  }, [hash, hashToken, navigate, pathname, search, tokenFromParams]);

  useEffect(() => {
    const previousRobotsMeta = document.querySelector('meta[name="robots"]')?.getAttribute('content') ?? null;
    const previousReferrerMeta = document.querySelector('meta[name="referrer"]')?.getAttribute('content') ?? null;

    let robotsMeta = document.querySelector('meta[name="robots"]');
    if (!robotsMeta) {
      robotsMeta = document.createElement('meta');
      robotsMeta.setAttribute('name', 'robots');
      document.head.appendChild(robotsMeta);
    }
    robotsMeta.setAttribute('content', 'noindex,nofollow');

    let referrerMeta = document.querySelector('meta[name="referrer"]');
    if (!referrerMeta) {
      referrerMeta = document.createElement('meta');
      referrerMeta.setAttribute('name', 'referrer');
      document.head.appendChild(referrerMeta);
    }
    referrerMeta.setAttribute('content', 'no-referrer');

    return () => {
      if (previousRobotsMeta === null) {
        robotsMeta?.remove();
      } else {
        robotsMeta?.setAttribute('content', previousRobotsMeta);
      }

      if (previousReferrerMeta === null) {
        referrerMeta?.remove();
      } else {
        referrerMeta?.setAttribute('content', previousReferrerMeta);
      }
    };
  }, []);

  const { data, isLoading, isFetching, error, refetch } = useGetPublicArtifactCollectionQuery(token || '', {
    skip: !token,
    refetchOnFocus: true,
    refetchOnReconnect: true
  });

  const brandedOrigin = useMemo(() => {
    const normalizedSignupUrl = ensureHttp(data?.branding.signupUrl || undefined);
    if (!normalizedSignupUrl) {
      return undefined;
    }

    try {
      return new URL(normalizedSignupUrl).origin;
    } catch {
      return normalizedSignupUrl;
    }
  }, [data?.branding.signupUrl]);
  const shouldLoadPartnerBranding = Boolean(
    brandedOrigin && (!data?.branding.colorPrimary || !data?.branding.colorPrimaryLighter)
  );
  const { data: partnerDesignConfig, isLoading: isPartnerBrandingLoading } = useGetDesignConfigQuery(
    { origin: brandedOrigin || '' },
    {
      skip: !shouldLoadPartnerBranding,
      refetchOnFocus: true,
      refetchOnReconnect: true
    }
  );

  const branding = useMemo(() => {
    if (!data?.branding) {
      return data?.branding;
    }

    return {
      ...data.branding,
      signupUrl: data.branding.signupUrl || partnerDesignConfig?.domainUrl || null,
      tenantOrganizationName: data.branding.tenantOrganizationName || partnerDesignConfig?.companyName || null,
      tenantOrganizationLogoUrl: data.branding.tenantOrganizationLogoUrl || partnerDesignConfig?.logoUrl || null,
      colorPrimary: data.branding.colorPrimary || partnerDesignConfig?.colorPrimary || null,
      colorPrimaryLighter: data.branding.colorPrimaryLighter || partnerDesignConfig?.colorPrimaryLighter || null
    };
  }, [data?.branding, partnerDesignConfig]);

  const heading = branding?.heroTitle || branding?.fullName || 'Artifacts';
  const errorStatus = Number((error as { status?: number | string } | undefined)?.status);
  const isInvalidToken = errorStatus === 400 || errorStatus === 404;
  const hasExpiredShare = data?.share?.status === 'EXPIRED';
  const hasRevokedShare = data?.share?.status === 'REVOKED';
  const isInactiveShare = errorStatus === 410 || hasExpiredShare || hasRevokedShare;
  const isGenericError = Boolean(error) && !isInvalidToken && !isInactiveShare;

  if (isLoading || (shouldLoadPartnerBranding && isPartnerBrandingLoading)) {
    return (
      <PublicPageShell>
        <ContainerComponent>
          <div style={{ padding: '48px 0' }}>
            <Skeleton active paragraph={{ rows: 6 }} />
          </div>
        </ContainerComponent>
      </PublicPageShell>
    );
  }

  if (!token || isInvalidToken || isInactiveShare || isGenericError) {
    const title = !token
      ? 'Invalid link'
      : hasExpiredShare
        ? 'This artifact link has expired.'
        : hasRevokedShare
          ? 'This artifact link has been revoked.'
          : isInactiveShare
            ? 'This artifact link is no longer active.'
            : isInvalidToken
              ? 'Invalid artifact link'
              : 'This artifact collection is temporarily unavailable.';
    const description = !token
      ? 'This artifact link is missing or incomplete.'
      : hasExpiredShare
        ? 'Please contact the sender if you need a fresh link to this collection.'
        : hasRevokedShare
          ? 'The sender has turned off access to this collection. Please ask for a new link if needed.'
          : isInactiveShare
            ? 'Please contact the sender if you need a new public collection link.'
            : isInvalidToken
              ? 'The link may be incorrect, expired, or no longer available.'
              : 'Please try again in a moment.';

    return (
      <PublicPageShell>
        <ContainerComponent>
          <div style={{ padding: '40px 0 56px' }}>
            <SpaceBlock title={heading} subtitle={branding?.heroSubtitle}>
              <Alert type="error" showIcon message={title} description={description} />
              {token && (
                <Button style={{ marginTop: 16 }} onClick={() => refetch()}>
                  Refresh page
                </Button>
              )}
            </SpaceBlock>
          </div>
        </ContainerComponent>
      </PublicPageShell>
    );
  }

  return (
    <PublicPageShell>
      <ContainerComponent>
        <div style={{ padding: '32px 0 48px' }}>
          {!isFetching && data && !data.collection.items.length && (
            <SpaceBlock title={heading} subtitle={branding?.heroSubtitle}>
              <Empty description="This collection does not have any artifacts to display yet." />
            </SpaceBlock>
          )}

          {data && data.collection.items.length > 0 && (
            <PublicArtifactCollectionRenderer
              branding={branding || data.branding}
              items={data.collection.items}
              publicToken={token}
              signupCode={data.signupCode}
              {...(data.allowDownloads ? { allowDownloads: true } : {})}
              {...(data.downloadZipUrl ? { downloadZipUrl: data.downloadZipUrl } : {})}
            />
          )}
        </div>
      </ContainerComponent>
    </PublicPageShell>
  );
}

function SpaceBlock({ children, subtitle, title }: { children: ReactNode; subtitle?: string | null; title: string }) {
  return (
    <div>
      <Title level={1}>{title}</Title>
      {subtitle && <Paragraph>{subtitle}</Paragraph>}
      {children}
    </div>
  );
}

function PublicPageShell({ children }: { children: ReactNode }) {
  return <div className="layout-full-height">{children}</div>;
}

export default PublicArtifactCollectionPage;
